import type { FC, PropsWithChildren, ReactNode } from 'react';
import type { SPOT_ILLUSTRATION } from '@george-labs.com/george-assets/illustrations';
import type { ICON } from '@george-labs.com/george-assets/icons';
import {
    CardBody,
    Icon,
    Inline,
    Paragraph,
    SpotIllustration,
} from '@george-labs.com/design-system';

import { HighlightContext } from './HighlightContext';

export type HighlightCardProps = {
    'data-cy'?: string;
    icon?: `${ICON}`;
    label: ReactNode;
    value: ReactNode;
    valuePostfix?: ReactNode;
    illustration?: `${SPOT_ILLUSTRATION}`;
};

export const HighlightCard: FC<PropsWithChildren<HighlightCardProps>> = ({
    'data-cy': dataCy,
    icon,
    label,
    value,
    valuePostfix,
    children,
    illustration,
}) => (
    <HighlightContext value={{ dataCy }}>
        <CardBody className="text-center" data-cy={`${dataCy}-highlight-card`}>
            {icon && (
                <Icon
                    assetIcon={icon as ICON}
                    size={Icon.SIZE.SIZE_48}
                    className="mt-3 g-faded"
                    data-cy={`${dataCy}-highlight-icon`}
                />
            )}
            {illustration && (
                <SpotIllustration
                    className="mx-auto"
                    ornamental
                    illustration={illustration as SPOT_ILLUSTRATION}
                    data-cy={`${dataCy}-highlight-illustration`}
                />
            )}
            <Paragraph faded>
                <Inline emphasis data-cy={`${dataCy}-highlight-label`}>
                    {label}
                </Inline>
            </Paragraph>
            <Paragraph>
                {value}
                {valuePostfix && <>{valuePostfix}</>}
            </Paragraph>
            {children}
        </CardBody>
    </HighlightContext>
);
